import * as React from "react";
import { useMutation } from "@tanstack/react-query";

import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { useAuth } from "@/contexts/auth-context";
import { apiFetch } from "@/lib/api-client";
import { USER_ORIGIN_LEARNWORLDS_NEW } from "@/lib/user-origin";
import {
  toChangeEmailError,
  type ChangeEmailBanner,
  type ChangeEmailFieldErrors,
} from "@/components/account/change-email-error";

// The SELF-SERVICE change-email dialog (Story 14.9 AC5; mounted by the org_admin
// Settings screen and by Screen 14's Professional details card, 14.10 AC9).
//
// Deliberately NOT the same component as `routes/org-admin/ChangeEmailDialog.tsx`:
// that one is an org_admin acting ON BEHALF OF a clinician (different endpoint,
// no password confirmation, third-person copy). They are never rendered together
// and share no props; merging them would mean a props union whose invalid
// combinations outnumber the valid ones.
//
// The password confirmation is what makes this safe to expose to a session: a
// stolen access token alone must not be enough to move the sign-in address.

/** Mirrors the endpoint's `ChangeEmailData` (a `ChangePasswordResponse` twin). */
interface ChangeEmailResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user_id: string;
  email: string;
  role: string;
  org_id: string;
}

/**
 * AC5 — shown BEFORE submit for a `learnworlds_new` caller only. The address is
 * what LearnWorlds matches on (Begin-Calibration attach, year-2 renewal
 * attribution), so a change made only here silently splits the two records. The
 * change itself is never blocked; mismatches land in the existing manual-review
 * paths. Second person — this is the account owner, not an administrator.
 */
export const SELF_LEARNWORLDS_EMAIL_WARNING =
  "Your account is matched to LearnWorlds by email address. Changing it here does not change it on LearnWorlds — use the new address there too, or your course progress won't attach.";

export function ChangeEmailDialog({
  currentEmail,
  onCancel,
  onChanged,
}: {
  currentEmail: string;
  onCancel: () => void;
  /** Called with the address the SERVER confirmed, never the typed draft. */
  onChanged: (newEmail: string) => void;
}) {
  const { user, setSession } = useAuth();
  const [newEmail, setNewEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [banner, setBanner] = React.useState<ChangeEmailBanner | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<ChangeEmailFieldErrors>({});

  const changeEmail = useMutation({
    mutationFn: (values: { newEmail: string; password: string }) =>
      apiFetch<ChangeEmailResponse>("/profile/change-email", {
        method: "POST",
        body: {
          new_email: values.newEmail,
          password_confirmation: values.password,
        },
        // skipAuthRefresh: a wrong-password 401 (AUTH-001) must surface inline,
        // not ride the silent-refresh → logout path.
        skipAuthRefresh: true,
      }),
    onSuccess: async (data) => {
      // Adopt the fresh token FIRST — the server has already burned every other
      // session, so the old refresh cookie is dead by the time this resolves.
      await setSession(data.access_token);
      onChanged(data.email);
    },
    onError: (error) => {
      const mapped = toChangeEmailError(error);
      setFieldErrors(mapped.fieldErrors);
      setBanner(mapped.banner);
    },
  });

  const trimmedEmail = newEmail.trim();
  const canSubmit =
    trimmedEmail.length > 0 && password.length > 0 && !changeEmail.isPending;

  function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;
    setBanner(null);
    setFieldErrors({});
    changeEmail.mutate({ newEmail: trimmedEmail, password });
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="w-[min(var(--width-md,520px),100%)]">
        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <DialogHeader>
            <DialogTitle>Change email</DialogTitle>
            <DialogDescription>
              {`The new address becomes your sign-in email immediately. A notification is sent to ${currentEmail}.`}
            </DialogDescription>
          </DialogHeader>

          {user?.userOrigin === USER_ORIGIN_LEARNWORLDS_NEW ? (
            <Banner variant="warn" title="Also update it on LearnWorlds">
              {SELF_LEARNWORLDS_EMAIL_WARNING}
            </Banner>
          ) : null}

          <FormField
            label="New email"
            error={fieldErrors.newEmail}
            htmlFor="change-email-new-email"
            required
          >
            <Input
              id="change-email-new-email"
              type="email"
              autoComplete="off"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
          </FormField>

          <FormField
            label="Confirm with your password"
            error={fieldErrors.passwordConfirmation}
            htmlFor="change-email-password"
            required
          >
            <PasswordInput
              id="change-email-password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </FormField>

          {banner ? (
            <Banner variant={banner.variant} title={banner.title}>
              {banner.body}
            </Banner>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {changeEmail.isPending ? "Changing email…" : "Change email"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
